// apps/client/src/components/GameTimer.tsx
//
// Countdown timer for WAITING state
//

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface GameTimerProps {
    timeUntilStart: number | null; // in seconds
    status: string;
}

export function GameTimer({ timeUntilStart, status }: GameTimerProps) {
    const [seconds, setSeconds] = useState(timeUntilStart ?? 0);

    useEffect(() => {
        if (timeUntilStart !== null) {
            setSeconds(timeUntilStart);
        }
    }, [timeUntilStart]);

    useEffect(() => {
        if (status !== 'WAITING' || seconds <= 0) return;

        const timer = setInterval(() => {
            setSeconds((prev) => Math.max(0, prev - 1));
        }, 1000);

        return () => clearInterval(timer);
    }, [status, seconds]);

    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;

    const formatTime = (n: number) => n.toString().padStart(2, '0');

    if (status === 'PLAYING') {
        return (
            <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center"
            >
                <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold">
                    <span className="animate-pulse">🎱</span>
                    <span>JUEGO EN CURSO</span>
                    <span className="animate-pulse">🎱</span>
                </div>
            </motion.div>
        );
    }

    if (status === 'FINISHED') {
        return (
            <div className="text-center">
                <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gray-700 text-gray-300 font-bold">
                    🏁 Game Over
                </div>
            </div>
        );
    }

    return (
        <div className="text-center">
            <p className="text-gray-400 mb-2">Next game in</p>
            <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: [0.95, 1, 0.95] }}
                transition={{ duration: 1, repeat: Infinity }}
                className="inline-flex items-center gap-1"
            >
                <div className="bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl px-4 py-3 text-3xl font-mono font-bold text-white min-w-[4rem] text-center">
                    {formatTime(minutes)}
                </div>
                <span className="text-3xl font-bold text-pink-500 animate-pulse">:</span>
                <div className="bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl px-4 py-3 text-3xl font-mono font-bold text-white min-w-[4rem] text-center">
                    {formatTime(secs)}
                </div>
            </motion.div>
            <p className="text-sm text-gray-500 mt-2">
                ¡Get your card before it starts!
            </p>
        </div>
    );
}
